import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import FAQSection from "@/components/products/FAQSection";
import { submitToZoho } from "@/utils/secureFormSubmission";

const SMB = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.company) return;

    setIsSubmitting(true);
    try {
      const success = await submitToZoho({
        ...formData,
        formType: "SMB Solution"
      });

      if (success) {
        toast.success("Form submitted successfully! We'll be in touch soon.");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          message: ""
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <section className="bg-gradient-to-b from-slate-50 to-white py-16 md:py-24">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Small & Medium Business <span className="text-[#8932c8]">Cybersecurity</span>
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Enterprise-grade security tailored for small and medium businesses. Protect your business with Spharaka's affordable and powerful security solutions.
                </p>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-[#8932c8] hover:bg-[#7a2cb3] px-8 py-6 text-lg">
                      Request SMB Demo
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold">Request SMB Demo</DialogTitle>
                      <DialogDescription>
                        Fill out the form below and we'll show you how Spharaka works for small and medium businesses.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="firstName" className="block text-sm font-medium mb-1">First Name</label>
                          <Input 
                            id="firstName" 
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="John" 
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block text-sm font-medium mb-1">Last Name</label>
                          <Input 
                            id="lastName" 
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Doe" 
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-1">Work Email</label>
                        <Input 
                          id="email" 
                          type="email" 
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john.doe@yourbusiness.com" 
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium mb-1">Company</label>
                        <Input 
                          id="company" 
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Business LLC" 
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium mb-1">Business Security Needs</label>
                        <Textarea 
                          id="message" 
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your business security challenges..." 
                        />
                      </div>
                      <Button type="submit" className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]">
                        Submit Request
                      </Button>
                      <p className="text-xs text-gray-500 text-center mt-4">
                        By submitting this form, you agree to our privacy policy and terms of service.
                      </p>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?auto=format&fit=crop&w=1600&q=80"
                  alt="SMB Security"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">SMB Security Challenges</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-slate-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-4">Limited Resources</h3>
                <p className="text-gray-600">
                  Small and medium businesses often lack dedicated security teams and budgets for comprehensive security solutions.
                </p>
              </div>
              <div className="bg-slate-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-4">Growing Attack Surface</h3>
                <p className="text-gray-600">
                  Rapid digital transformation and cloud adoption create new vulnerabilities without proper security controls.
                </p>
              </div>
              <div className="bg-slate-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-4">Targeted Attacks</h3>
                <p className="text-gray-600">
                  Cybercriminals increasingly target SMBs as they often have valuable data with fewer protections than enterprises.
                </p>
              </div>
            </div>
          </div>
        </section>

        <FAQSection />

        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Spharaka XDR for SMB</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <img
                  src="https://images.unsplash.com/photo-1559581958-df379578606a?auto=format&fit=crop&w=1600&q=80"
                  alt="SMB XDR Solution"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              <div className="space-y-6 order-1 md:order-2">
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Affordable Protection</h3>
                  <p className="text-gray-600">
                    Enterprise-grade security at SMB-friendly prices with flexible subscription models.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Easy Deployment</h3>
                  <p className="text-gray-600">
                    Cloud-based solution that deploys in minutes with minimal configuration, even without security expertise.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Automated Security</h3>
                  <p className="text-gray-600">
                    AI-powered detection and response that works 24/7 without requiring dedicated security staff.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Simplified Management</h3>
                  <p className="text-gray-600">
                    Intuitive dashboard designed for business owners and IT generalists, not security specialists.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-[#8932c8] text-white">
          <div className="container mx-auto px-4 md:px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Protect your business today</h2>
            <p className="text-xl mb-10 max-w-3xl mx-auto">
              Join hundreds of SMBs that trust Spharaka to protect their critical assets and data.
            </p>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" className="bg-white text-[#8932c8] hover:bg-gray-100 border-white px-8 py-6 text-lg">
                  Get Started
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">Get Started with Spharaka</DialogTitle>
                  <DialogDescription>
                    Fill out the form below to get started with Spharaka for your small or medium business.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium mb-1">First Name</label>
                      <Input 
                        id="firstName" 
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John" 
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium mb-1">Last Name</label>
                      <Input 
                        id="lastName" 
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe" 
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-1">Work Email</label>
                    <Input 
                      id="email" 
                      type="email" 
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john.doe@yourbusiness.com" 
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-1">Company</label>
                    <Input 
                      id="company" 
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Business LLC" 
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-1">Message</label>
                    <Textarea 
                      id="message" 
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What specific security challenges is your business facing?" 
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]">
                    Submit
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-4">
                    By submitting this form, you agree to our privacy policy and terms of service.
                  </p>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SMB;
